import type { NavigateFunction } from "react-router-dom"
import type { ContextFetchResult } from "../../types/contextFetchResult"
import { clearCsrfToken, clientFetch } from "../../utils/clientFetch"

export const PATH_INFO = {
  LOGIN: {
    PATH: "",
  },
  COMPANY_LIST: {
    PATH: "/company/list",
  },
  ERROR: {
    PATH: "/error",
  },
  COMPANY_MANAGE: {
    PATH: "/company/:companyId/manage",
  },
  COMPANY_INFO: {
    PATH: "/company/:companyId/info",
  },
  COMPANY_DEVICES_LIST: {
    PATH: "/company/:companyId/device-list",
  },
  COMPANY_DEVICE_MANAGE: {
    PATH: "/company/:companyId/device-manage/:deviceId",
  },
  COMPANY_MEASUREMENTS_LIST: {
    PATH: "/company/:companyId/measurements-list",
  },
  DEPARTMENT_LIST: {
    PATH: "/company/:companyId/department-list",
  },
  DEPARTMENT_MANEGE: {
    PATH: "/company/:companyId/department-manage/:departmentId",
  },
  DEPARTMENT_DETAIL: {
    PATH: "/company/:companyId/department-detail/:departmentId",
  },
  DEPARTMENT_USER_DETAIL: {
    PATH: "/company/:companyId/department-user-detail/:departmentId/:userId",
  },
  DEPARTMENT_USER_MANAGE: {
    PATH: '/company/:companyId/department-user-manage/:departmentId/:userId'
  },
  DEVICE_LIST: {
    PATH: "/device/list",
  },
  DEVICE_MANAGE: {
    PATH: "/device/:deviceId/manage",
  },
  MEASUREMENTS_ERROR: {
    PATH: '/company/:companyId/measurements-error-settings'
  },
  NAME_MATCHING: {
    PATH: '/name-matching/manage'
  }
};

type AdminUserNavigationState = {
  backTo?: string
  returnTo?: string
  useHistoryBack?: boolean
}

type AdminUserNavigationStateRecord = {
  backTo?: unknown
  returnTo?: unknown
  useHistoryBack?: unknown
}

const ordinalRules = new Intl.PluralRules('en-US', { type: 'ordinal' })
const ordinalSuffixMap: Record<Intl.LDMLPluralRule, string> = {
  one: 'st',
  two: 'nd',
  few: 'rd',
  other: 'th',
  many: 'th',
  zero: 'th'
}

export function formatOrdinal(num: number) {
  const rule = ordinalRules.select(num)
  const suffix = ordinalSuffixMap[rule] ?? ordinalSuffixMap.other
  return `${num}${suffix}`
}

function parseInternalPathCandidate(value: unknown) {
  if (typeof value !== "string") {
    return undefined
  }

  const trimmed = value.trim()
  if (trimmed === "" || !trimmed.startsWith("/")) {
    return undefined
  }

  return trimmed
}

function parseAdminUserNavigationState(state: unknown): AdminUserNavigationStateRecord | undefined {
  if (!state || typeof state !== "object") {
    return undefined
  }

  return state as AdminUserNavigationStateRecord
}

function normalizeRouteParam(value: string | number | undefined | null) {
  if (value === undefined || value === null) {
    return undefined
  }

  const trimmed = String(value).trim()
  if (trimmed === "") {
    return undefined
  }

  return trimmed
}

export function buildCompanyInfoPath(companyId?: string) {
  if (!companyId) {
    return undefined
  }

  return PATH_INFO.COMPANY_INFO.PATH.replace(":companyId", companyId)
}

export function buildDepartmentDetailPath(companyId?: string, departmentId?: string) {
  if (!companyId || !departmentId || departmentId === "undefined") {
    return undefined
  }

  return PATH_INFO.DEPARTMENT_DETAIL.PATH
    .replace(":companyId", companyId)
    .replace(":departmentId", departmentId)
}

export function buildDepartmentUserDetailPath(
  companyId?: string,
  departmentId?: string,
  userId?: string | number | null
) {
  const trimmedUserId = normalizeRouteParam(userId)
  if (
    !companyId
    || !departmentId
    || departmentId === "undefined"
    || !trimmedUserId
    || trimmedUserId === "register"
  ) {
    return undefined
  }

  return PATH_INFO.DEPARTMENT_USER_DETAIL.PATH
    .replace(":companyId", companyId)
    .replace(":departmentId", departmentId)
    .replace(":userId", trimmedUserId)
}

export function buildDepartmentUserManagePath(
  companyId?: string,
  departmentId?: string,
  userId?: string | number | null
) {
  const trimmedUserId = normalizeRouteParam(userId)
  if (!companyId || !departmentId || !trimmedUserId) {
    return undefined
  }

  return PATH_INFO.DEPARTMENT_USER_MANAGE.PATH
    .replace(":companyId", companyId)
    .replace(":departmentId", departmentId)
    .replace(":userId", trimmedUserId)
}

export function resolveAdminUserBackToPath(
  state: unknown,
  companyId?: string,
  departmentId?: string
) {
  const record = parseAdminUserNavigationState(state)

  return parseInternalPathCandidate(record?.backTo)
    ?? buildDepartmentDetailPath(companyId, departmentId)
    ?? buildCompanyInfoPath(companyId)
}

export function resolveAdminUserExplicitBackToPath(state: unknown) {
  const record = parseAdminUserNavigationState(state)
  return parseInternalPathCandidate(record?.backTo)
}

export function resolveAdminUserReturnToPath(
  state: unknown,
  companyId?: string,
  departmentId?: string,
  userId?: string
) {
  const record = parseAdminUserNavigationState(state)

  return parseInternalPathCandidate(record?.returnTo)
    ?? resolveAdminUserBackToPath(state, companyId, departmentId)
}

export function shouldUseAdminUserHistoryBack(state: unknown) {
  const record = parseAdminUserNavigationState(state)
  return record?.useHistoryBack === true
}

export function buildAdminUserNavigationState(
  backTo?: string,
  returnTo?: string,
  useHistoryBack = false
): AdminUserNavigationState | undefined {
  const state: AdminUserNavigationState = {}
  const normalizedBackTo = parseInternalPathCandidate(backTo)
  const normalizedReturnTo = parseInternalPathCandidate(returnTo)

  if (normalizedBackTo) {
    state.backTo = normalizedBackTo
  }

  if (normalizedReturnTo) {
    state.returnTo = normalizedReturnTo
  }

  if (useHistoryBack) {
    state.useHistoryBack = true
  }

  return Object.keys(state).length > 0 ? state : undefined
}

export async function handleUnauthorized(navigate: NavigateFunction) {
  try {
    await clientFetch(`${process.env.REACT_APP_API_ENDPOINT}/apis/admin/logout`, {
      method: "POST",
    })
  } catch (error) {
    console.error("ログアウト処理に失敗しました。", error)
  } finally {
    clearCsrfToken()
    navigate(PATH_INFO.LOGIN.PATH)
  }
}

export function shouldStopForInvalidContextFetchResult<T>(
  result: ContextFetchResult<T>,
  navigate: NavigateFunction
) {
  // 401はclientFetch側の共通未認証ハンドラに任せ、それ以外の不正レスポンスだけエラー画面へ送る。
  if (result.status === 401) {
    return true
  }

  if (result.status !== 200 || !result.data) {
    navigate(PATH_INFO.ERROR.PATH, { replace: true })
    return true
  }

  return false
}

export const MEASUREMENT_LIST = [
  "AH(直線)",
  "ウエスト",
  "カフス幅",
  "ヒップ",
  "リブ(袖口)",
  "リブ幅(裾)",
  "リブ幅(袖口)",
  "リブ幅(袖口)(右)",
  "リブ幅(袖口)(左)",
  "わたり幅",
  "衿幅",
  "襟幅",
  "肩幅",
  "股下",
  "身幅",
  "裾幅",
  "裾幅(右)",
  "裾幅(左)",
  "前下がり",
  "前股ぐり",
  "前股上",
  "総丈",
  "袖巾",
  "袖口幅(右)",
  "袖口幅(左)",
  "袖丈",
  "袖丈(右)",
  "袖丈(左)",
  "袖幅",
  "着丈(CB)",
  "着丈(NP)",
  "天幅",
  "膝幅",
  "裄丈",
  "裄丈(右)",
  "裄丈(左)",
  "ボタン間隔",
]
